import React, { useEffect, useState } from 'react';
import {
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Avatar,
  Badge,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import CloseIcon from '@mui/icons-material/Close';
import { auth, db } from '../firebase';
import { doc, getDoc, collection, onSnapshot, query, updateDoc, serverTimestamp } from 'firebase/firestore';

interface User {
  email: string;
  color: string;
  online: boolean;
  lastSeen: Date;
}

interface UserListProps {
  drawerWidth?: number;
}

export const UserList: React.FC<UserListProps> = ({ drawerWidth = 240 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState<Record<string, User>>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!auth.currentUser) return;

    // 현재 사용자의 온라인 상태 관리
    const currentUserRef = doc(db, 'users', auth.currentUser.uid);

    const setUserOnlineStatus = async () => {
      try {
        const userDoc = await getDoc(currentUserRef);
        if (userDoc.exists()) {
          await updateDoc(currentUserRef, {
            online: true,
            lastSeen: serverTimestamp()
          });
        }
      } catch (error) {
        console.error('Error setting user online status:', error);
      }
    };

    setUserOnlineStatus();

    // 모든 사용자의 상태 모니터링
    const usersQuery = query(collection(db, 'users'));
    const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
      const usersData: Record<string, User> = {};
      snapshot.forEach((doc) => {
        const data = doc.data();
        usersData[doc.id] = {
          email: data.email,
          color: data.color || '#757575',
          online: data.online || false,
          lastSeen: data.lastSeen?.toDate() || new Date()
        };
      });
      setUsers(usersData);
    });

    // 브라우저 종료 또는 페이지 이탈 시 오프라인 상태로 변경
    const handleBeforeUnload = () => {
      if (auth.currentUser) {
        updateDoc(currentUserRef, {
          online: false,
          lastSeen: serverTimestamp()
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      unsubscribe();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // 컴포넌트 언마운트 시 오프라인으로 설정
      if (auth.currentUser) {
        updateDoc(currentUserRef, {
          online: false,
          lastSeen: serverTimestamp()
        });
      }
    };
  }, [auth.currentUser]);

  const toggleDrawer = () => {
    setIsOpen(!isOpen);
  };

  const onlineCount = Object.values(users).filter(user => user.online).length;

  return (
    <>
      <IconButton 
        onClick={toggleDrawer}
        sx={{ 
          color: 'inherit',
          position: 'relative',
        }}
      >
        <Badge 
          badgeContent={onlineCount} 
          color="success"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: theme.palette.success.main,
            }
          }}
        >
          <PeopleIcon />
        </Badge>
      </IconButton>

      <Drawer
        anchor={isMobile ? 'right' : 'left'}
        open={isOpen}
        onClose={toggleDrawer}
        variant={isMobile ? 'temporary' : 'persistent'}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">
            접속자 목록 ({onlineCount})
          </Typography>
          {isMobile && (
            <IconButton onClick={toggleDrawer}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        <List sx={{ pt: 0 }}>
          {Object.entries(users).length > 0 ? (
            Object.entries(users).map(([uid, user]) => (
              <ListItem key={uid}>
                <ListItemAvatar>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    variant="dot"
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: user.online ? theme.palette.success.main : theme.palette.grey[500],
                      }
                    }}
                  >
                    <Avatar sx={{ bgcolor: user.color }}>
                      {user.email?.[0].toUpperCase()}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography>
                      {user.email}
                      {uid === auth.currentUser?.uid && (
                        <Typography component="span" color="text.secondary">
                          {' '}(나)
                        </Typography>
                      )}
                    </Typography>
                  }
                  secondary={user.online ? '온라인' : '오프라인'}
                />
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText primary="접속자 목록을 불러오는 중..." />
            </ListItem>
          )}
        </List>
      </Drawer>
    </>
  );
}; 